export type VerifiedRating = {
  value: number;
  reviewCount: number;
};

export const BUSINESS_DATA = {
  siteUrl: "https://lakewoodmedspa.com",
  name: "Lakewood Med Spa",
  // "Lakewood Medspa" (one word) is how the Google Business Profile and several directories spell the
  // name; listing it here tells engines both spellings are one entity without editing the Google profile.
  alternateName: ["Lakewood Med Spa Dallas", "Lakewood Medspa"],
  phone: "+1-214-247-6793",
  phoneDisplay: "(214) 247-6793",
  email: "contact@lakewoodmedspa.com",
  bookingUrl: "https://medsparecord.com/book?step=0",
  logoUrl:
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663381010537/AX6sNuigosh2pncBHVxRxQ/logo_optimized_1ee89f17.webp",
  physicianImageUrl:
    "https://lakewoodmedspa.com/images/co2-lp-dr-kohli-620.webp",
  address: {
    streetAddress: "8210 Walnut Hill Ln, Professional 1, #516",
    locality: "Dallas",
    region: "TX",
    postalCode: "75231",
    country: "US",
  },
  campus: "Texas Health Presbyterian Hospital Dallas campus",
  geo: { latitude: 32.8818477, longitude: -96.7611209 },
  hours: [
    {
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
      display: "Mon–Fri: 9am–5pm",
    },
    {
      days: ["Saturday"],
      opens: "10:00",
      closes: "14:00",
      display: "Sat: 10am–2pm",
    },
  ],
  mapUrl:
    "https://www.google.com/maps/place/Lakewood+Medspa/@32.8818477,-96.7611209,17z/data=!4m6!3m5!1s0x864e9f91e91fb369:0x416224a94d3ac04a!8m2!3d32.8818477!4d-96.7611209!16s%2Fg%2F11z60jgdgv",
  rating: { value: 4.9, reviewCount: 64 } as VerifiedRating | null, // Google Maps public listing, verified 2026-11-02 (was 59 on 2026-10-09)
  physician: {
    name: "Dr. Rajan Kohli, MD",
    credential: "Board-Certified in Family Medicine",
    pageUrl: "/dr-kohli",
  },
} as const;

export const DFW_SERVICE_AREAS = [
  { name: "Park Cities" },
  { name: "Preston Hollow" },
  { name: "Highland Park" },
  { name: "University Park" },
  { name: "Uptown Dallas" },
  { name: "Lakewood" },
  { name: "East Dallas" },
  { name: "Lake Highlands" },
  { name: "Richardson" },
  { name: "Plano" },
  { name: "Frisco" },
  { name: "Garland" },
  { name: "Downtown Dallas" },
] as const;

const postalAddress = () => ({
  "@type": "PostalAddress",
  streetAddress: BUSINESS_DATA.address.streetAddress,
  addressLocality: BUSINESS_DATA.address.locality,
  addressRegion: BUSINESS_DATA.address.region,
  postalCode: BUSINESS_DATA.address.postalCode,
  addressCountry: BUSINESS_DATA.address.country,
});

export function buildBusinessSchema(imageUrl: string = BUSINESS_DATA.physicianImageUrl) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "MedicalClinic", "HealthAndBeautyBusiness", "LocalBusiness"],
    "@id": `${BUSINESS_DATA.siteUrl}/#organization`,
    name: BUSINESS_DATA.name,
    alternateName: [...BUSINESS_DATA.alternateName],
    description:
      "Physician-led medical spa on the Texas Health Presbyterian Hospital Dallas campus offering aesthetic treatments, skin rejuvenation, body contouring, hair restoration, and physician-supervised wellness services.",
    url: BUSINESS_DATA.siteUrl,
    telephone: BUSINESS_DATA.phone,
    email: BUSINESS_DATA.email,
    logo: BUSINESS_DATA.logoUrl,
    image: imageUrl,
    priceRange: "$$–$$$",
    currenciesAccepted: "USD",
    address: postalAddress(),
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS_DATA.geo.latitude,
      longitude: BUSINESS_DATA.geo.longitude,
    },
    openingHoursSpecification: BUSINESS_DATA.hours.map(hours => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: hours.days,
      opens: hours.opens,
      closes: hours.closes,
    })),
    areaServed: DFW_SERVICE_AREAS.map(area => ({ "@type": "Place", name: `${area.name}, Texas` })),
    hasMap: BUSINESS_DATA.mapUrl,
    sameAs: [
      BUSINESS_DATA.mapUrl,
      "https://www.instagram.com/lakewoodmedspadallas",
      "https://www.facebook.com/lakewoodmedspadallas",
      "https://g.page/r/CUrAOk2pJGJBEAE/review",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Lakewood Med Spa Treatments",
      itemListElement: [
        ["Botox / Neuromodulators", "/botox"],
        ["Dermal Fillers", "/dermal-fillers"],
        ["Lip Fillers", "/lip-fillers"],
        ["Kybella", "/kybella"],
        ["PDO Threads", "/pdo-threads"],
        ["CO₂ Laser Resurfacing", "/fractional-co2"],
        ["DuoVive", "/duovive"],
        ["RF Microneedling", "/rf-microneedling"],
        ["Collagen Microneedling", "/microneedling"],
        ["Microneedling + PRP", "/rf-microneedling"],
        ["IPL / Photofacial", "/dpl-photofacial"],
        ["Pico Laser", "/pico-laser"],
        ["AquaFacial", "/aquafacial"],
        ["Chemical Peels", "/chemical-peels"],
        ["PRP Hair Restoration", "/hair-growth"],
        ["Shockwave Therapy", "/pain-regenerative-medicine"],
        ["PRP Joint Therapy", "/pain-regenerative-medicine"],
        ["Medical Weight Loss", "/weight-loss"],
        ["XERF Structural Skin Tightening", "/xerf-skin-tightening"],
        ["Botox Membership", "/membership"],
        ["Laser Hair Removal", "/areslite-hair-removal"],
      ].map(([name, path]) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name, url: `${BUSINESS_DATA.siteUrl}${path}` },
        url: `${BUSINESS_DATA.siteUrl}${path}`,
      })),
    },
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: BUSINESS_DATA.bookingUrl,
        actionPlatform: ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"],
      },
    },
  };

  if (BUSINESS_DATA.rating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: BUSINESS_DATA.rating.value.toFixed(1),
      reviewCount: BUSINESS_DATA.rating.reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  return schema;
}

/**
 * Dr. Kohli's topics, as listed on /dr-kohli (the page imports this list, so the visible text and the
 * schema cannot drift).
 */
export const PHYSICIAN_KNOWS_ABOUT = [
  "RF Microneedling & Skin Tightening",
  "Fractional CO₂ Laser Resurfacing",
  "Botox & Neuromodulator Injections",
  "Dermal Fillers & Facial Contouring",
  "Medical Weight Loss (GLP-1 Protocols)",
  "Bioidentical Hormone Optimization",
  "Peptide Therapy & Anti-Aging Medicine",
  "Vaginal Rejuvenation & Intimate Wellness",
  "PRP Hair Restoration",
  "Body Contouring & Non-Invasive Fat Reduction",
] as const;

/**
 * Lakewood Primary Care & Wellness is a SEPARATE business (own tax ID, own phone) at the same address
 * (owner 2026-11-02). It is referenced only as another practice Dr. Kohli works for. Never model it as
 * a parent, child or alias of the med spa, and never reuse the med spa's @id for it.
 */
export const PRIMARY_CARE_PRACTICE = {
  id: "https://lakewoodprimarycarewellness.com/#organization",
  name: "Lakewood Primary Care & Wellness",
  url: "https://lakewoodprimarycarewellness.com/",
  phone: "+1-214-810-4331",
} as const;

export function buildPhysicianSchema(imageUrl: string = BUSINESS_DATA.physicianImageUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "Physician",
    "@id": `${BUSINESS_DATA.siteUrl}/#physician`,
    name: BUSINESS_DATA.physician.name,
    description:
      `${BUSINESS_DATA.physician.name} is ${BUSINESS_DATA.physician.credential} and Medical Director of Lakewood Med Spa in Dallas, Texas.`,
    url: `${BUSINESS_DATA.siteUrl}${BUSINESS_DATA.physician.pageUrl}`,
    image: imageUrl,
    telephone: BUSINESS_DATA.phone,
    medicalSpecialty: ["Family Medicine", "Aesthetic Medicine"],
    // Credentials below are the ones already published on /dr-kohli; no awarding body is named because
    // the site does not state one.
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", name: "Doctor of Medicine (MD)", credentialCategory: "degree" },
      { "@type": "EducationalOccupationalCredential", name: "Board Certification in Family Medicine", credentialCategory: "certification" },
      {
        "@type": "EducationalOccupationalCredential",
        name: "Fellowship training in Integrative, Functional & Regenerative Medicine",
        credentialCategory: "fellowship training",
      },
    ],
    knowsAbout: [...PHYSICIAN_KNOWS_ABOUT],
    worksFor: [
      { "@id": `${BUSINESS_DATA.siteUrl}/#organization` },
      {
        "@type": "MedicalClinic",
        "@id": PRIMARY_CARE_PRACTICE.id,
        name: PRIMARY_CARE_PRACTICE.name,
        url: PRIMARY_CARE_PRACTICE.url,
        telephone: PRIMARY_CARE_PRACTICE.phone,
        address: postalAddress(),
      },
    ],
    address: postalAddress(),
  };
}

export function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BUSINESS_DATA.siteUrl}/#website`,
    name: BUSINESS_DATA.name,
    url: BUSINESS_DATA.siteUrl,
    publisher: { "@id": `${BUSINESS_DATA.siteUrl}/#organization` },
  };
}

/**
 * `imageUrl` overrides the business + physician schema image for one route (routes with their own
 * link-preview image, see shared/routeOgImages.ts). Every other route keeps the default headshot.
 */
export function buildCoreBusinessSchemas(imageUrl?: string) {
  return [buildBusinessSchema(imageUrl), buildPhysicianSchema(imageUrl), buildWebsiteSchema()];
}
